import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import {
  getProceduresListRouteMatchOptions,
  SCROLL_RESTORATION_ROUTES,
  SCROLL_SCOPES_BY_MODULE,
  resolveProceduresActiveTab,
  setProceduresActiveTab,
} from "~/constants/scrollRestorationRoutes";
import { useScrollRestorationContext } from "~/contexts/ScrollRestorationContext";
import {
  clearModuleScrollStates,
  getMainScrollElement,
  getScrollModuleForPathname,
  matchDetailRoute,
  matchListRoute,
  persistScrollForScope,
  readScrollState,
  resolveScopeKey,
  restoreScrollForScope,
  SCROLL_KIND_VIRTUAL_TABLE,
} from "~/utils/scrollRestoration";

const throttle = <T extends unknown[]>(
  fn: (...args: T) => void,
  wait: number,
): ((...args: T) => void) => {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let lastArgs: T;
  return (...args: T) => {
    lastArgs = args;
    if (timeout) return;
    timeout = setTimeout(() => {
      fn(...lastArgs);
      timeout = undefined;
    }, wait);
  };
};

const syncProceduresTabFromState = (
  pathname: string,
  routerState: unknown,
): void => {
  if (pathname !== "/app/work/procedures") return;
  setProceduresActiveTab(
    resolveProceduresActiveTab(
      routerState as { activeTab?: string } | null | undefined,
    ),
  );
};

const ScrollRestorationHandler = () => {
  const location = useLocation();
  const { getScrollContainer } = useScrollRestorationContext() || {};
  const prevPathRef = useRef(location.pathname);

  const tryRestoreList = (pathname: string, routerState: unknown) => {
    syncProceduresTabFromState(pathname, routerState);
    const listRoute = matchListRoute(
      pathname,
      SCROLL_RESTORATION_ROUTES,
      getProceduresListRouteMatchOptions(pathname),
    );
    if (!listRoute) return;
    const scope = resolveScopeKey(listRoute, pathname);
    const saved = readScrollState(scope);
    if (!saved) return;
    if (!saved.isVirtualList && (saved.y ?? 0) <= 0 && (saved.x ?? 0) <= 0) {
      return;
    }
    if (saved.scrollKind === SCROLL_KIND_VIRTUAL_TABLE) {
      return;
    }
    if (saved.isVirtualList) {
      return;
    }
    if (saved.listQueryKey && (saved.y ?? 0) > 0) {
      return;
    }
    restoreScrollForScope(scope, getScrollContainer);
  };

  useLayoutEffect(() => {
    const prevPath = prevPathRef.current;
    const nextPath = location.pathname;
    if (prevPath === nextPath) return;

    syncProceduresTabFromState(nextPath, location.state);

    const prevList = matchListRoute(
      prevPath,
      SCROLL_RESTORATION_ROUTES,
      getProceduresListRouteMatchOptions(prevPath),
    );
    const nextList = matchListRoute(
      nextPath,
      SCROLL_RESTORATION_ROUTES,
      getProceduresListRouteMatchOptions(nextPath),
    );
    const prevDetail = matchDetailRoute(prevPath, SCROLL_RESTORATION_ROUTES);

    const prevModule = getScrollModuleForPathname(
      prevPath,
      SCROLL_RESTORATION_ROUTES,
      getProceduresListRouteMatchOptions(prevPath),
    );
    const nextModule = getScrollModuleForPathname(
      nextPath,
      SCROLL_RESTORATION_ROUTES,
      getProceduresListRouteMatchOptions(nextPath),
    );

    const returningToListInSameModule =
      prevDetail && nextList && prevModule && prevModule === nextModule;

    const crossModuleNavigation =
      prevModule && nextModule && prevModule !== nextModule;

    if (crossModuleNavigation) {
      clearModuleScrollStates(prevModule, SCROLL_SCOPES_BY_MODULE);
      clearModuleScrollStates(nextModule, SCROLL_SCOPES_BY_MODULE);
      const main = getMainScrollElement();
      if (main) {
        main.scrollTo(0, 0);
      }
    } else if (prevModule && !nextModule && !prevDetail) {
      clearModuleScrollStates(prevModule, SCROLL_SCOPES_BY_MODULE);
    }

    if (returningToListInSameModule) {
      tryRestoreList(nextPath, location.state);
    }

    prevPathRef.current = nextPath;
  }, [location.pathname, location.state, getScrollContainer]);

  useEffect(() => {
    const listRoute = matchListRoute(
      location.pathname,
      SCROLL_RESTORATION_ROUTES,
      getProceduresListRouteMatchOptions(location.pathname),
    );
    if (!listRoute) return undefined;

    const scope = resolveScopeKey(listRoute, location.pathname);
    const main = document.querySelector("main.overflow-y-auto");
    if (!main) return undefined;

    const onScroll = throttle(() => {
      const registered = getScrollContainer?.(scope);
      if (registered) {
        persistScrollForScope(scope, () => registered);
        return;
      }
      const mainEl = getMainScrollElement();
      if (!mainEl) return;
      if (mainEl.scrollHeight <= mainEl.clientHeight + 1) return;
      persistScrollForScope(scope, () => mainEl);
    }, 120);
    main.addEventListener("scroll", onScroll, { passive: true });

    return () => main.removeEventListener("scroll", onScroll);
  }, [location.pathname, getScrollContainer]);

  return null;
};

export default ScrollRestorationHandler;
